/**
 * A leaderboard with its sort tabs, paging, empty state, and failure state —
 * used by the home page and by every category and specialism board.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  MINIMUM_FIRST_BID_CENTS,
  type LeaderboardScope,
  type RankedEntry,
} from '@bidtohire/shared';

import { PUBLISH_FROM_LABEL, firstPlaceTotalCents } from '../lib/bid-pricing';
import { useLeaderboard } from '../lib/queries';
import { Leaderboard } from './Leaderboard';
import { BoardSkeleton, EmptyState, ErrorNotice } from './Status';
import { Money } from './Money';

type Sort = 'paid' | 'recent' | 'newest' | 'salary';

const SORTS: readonly { readonly key: Sort; readonly label: string }[] = [
  { key: 'paid', label: 'Top paid' },
  { key: 'recent', label: 'Recently raised' },
  { key: 'newest', label: 'Newest' },
  { key: 'salary', label: 'Highest salary' },
];

/**
 * The board arrives ranked by amount paid, which is the product's ordering and
 * the only one the rank numbers describe. The other views re-order the rows
 * that have been fetched and keep each row's true position on its badge, so a
 * sort never misrepresents where a listing sits.
 */
export function reorderForTest(
  entries: readonly RankedEntry[],
  sort: Sort,
): readonly RankedEntry[] {
  return reorder(entries, sort);
}

function reorder(entries: readonly RankedEntry[], sort: Sort): readonly RankedEntry[] {
  if (sort === 'paid') return entries;
  const copy = [...entries];
  if (sort === 'recent') {
    return copy.sort(
      (a, b) => Date.parse(b.lastBidAt ?? b.createdAt) - Date.parse(a.lastBidAt ?? a.createdAt),
    );
  }
  if (sort === 'newest') {
    return copy.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
  }
  // Salary is only ever known from the label the listing published.
  return copy.sort((a, b) => salarySortKey(b) - salarySortKey(a));
}

/** Hours a year, days a year, and so on — enough to compare unlike periods. */
const PERIOD_MULTIPLIER: Readonly<Record<string, number>> = {
  hour: 2080,
  day: 260,
  week: 52,
  month: 12,
  year: 1,
};

/**
 * An annualised figure read back out of the published label: "$800 – $1.2k per
 * day" is 208000, not 800.
 *
 * The label is all the board projection carries, and it is compacted — a "k"
 * suffix above a thousand, the period spelled out after it. Comparing the bare
 * digit run scores that day rate as 800 and a "$220k per year" role as 220, so
 * a tab promising the highest salary would put the day rate above it.
 */
function salarySortKey(entry: RankedEntry): number {
  const label = entry.compensationLabel;
  if (label === undefined) return -1;
  const amount = label.match(/(\d[\d,]*(?:\.\d+)?)\s*([km])?/i);
  if (amount == null) return -1;
  const suffix = amount[2]?.toLowerCase();
  const scale = suffix === 'm' ? 1_000_000 : suffix === 'k' ? 1_000 : 1;
  const period = label.match(/per (hour|day|week|month|year)/i);
  const multiplier = period == null ? 1 : (PERIOD_MULTIPLIER[period[1]?.toLowerCase() ?? ''] ?? 1);
  return Number((amount[1] ?? '0').replace(/,/g, '')) * scale * multiplier;
}

export function BoardSection({
  scope,
  scopeName,
  showTabs = true,
  footNote,
}: {
  readonly scope: LeaderboardScope;
  readonly scopeName: string;
  readonly showTabs?: boolean;
  readonly footNote?: ReactNode;
}): ReactNode {
  const board = useLeaderboard(scope);
  const [sort, setSort] = useState<Sort>('paid');

  const entries = useMemo(
    () => (board.data?.pages ?? []).flatMap((page) => page.items),
    [board.data],
  );
  const sorted = useMemo(() => reorder(entries, sort), [entries, sort]);

  if (board.isLoading) return <BoardSkeleton />;
  if (board.isError) return <ErrorNotice error={board.error} onRetry={() => void board.refetch()} />;

  const totalListings = board.data?.pages[0]?.totalListings ?? entries.length;

  if (entries.length === 0) {
    return (
      <EmptyState
        headline={`No listings in ${scopeName} yet.`}
        action={
          <Link className="pill pill-ghost" to="/post">
            Post the first role — {PUBLISH_FROM_LABEL}
          </Link>
        }
      >
        No bids yet. The first <Money cents={MINIMUM_FIRST_BID_CENTS} /> takes the top spot.
      </EmptyState>
    );
  }

  const topSpot = firstPlaceTotalCents(entries.map((entry) => entry.totalPaidCents));

  return (
    <div className="stack" style={{ gap: 14 }}>
      {showTabs ? (
        <div className="tabs" role="tablist" aria-label="Sort listings">
          {SORTS.map((option) => (
            <button
              key={option.key}
              type="button"
              role="tab"
              aria-selected={sort === option.key}
              className={sort === option.key ? 'tab is-on' : 'tab'}
              onClick={() => setSort(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}

      <div className="card board">
        <Leaderboard entries={sorted} />

        <div className="board-foot">
          <span>
            {footNote ?? (
              <>
                Showing {entries.length} of {totalListings.toLocaleString('en-US')}{' '}
                {totalListings === 1 ? 'listing' : 'listings'} in {scopeName}. The top spot costs{' '}
                <Money cents={topSpot} />.
              </>
            )}
          </span>
          {board.hasNextPage ? (
            <button
              type="button"
              className="linkbtn"
              style={{ fontWeight: 500 }}
              onClick={() => void board.fetchNextPage()}
              disabled={board.isFetchingNextPage}
            >
              {board.isFetchingNextPage ? 'Loading…' : 'Load more listings →'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
